import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Plus, Search, DollarSign, Edit, Trash2, Mail, 
  CreditCard, Phone, UserCheck, Filter, MoreVertical, Wallet,
  Send,
  Square,
  CheckSquare,
  X,
  FileSpreadsheet,
  FileDown
} from 'lucide-react';
import { clientsApi, Client, salesApi } from '../services/api';
import { ShareProposalModal } from '../components/ShareProposalModal';
import { ExcelImportModal, ImportFieldDef } from '../components/ExcelImportModal';
import { exportToExcel } from '../utils/excel';
import toast from 'react-hot-toast';

const CLIENT_IMPORT_FIELDS: ImportFieldDef[] = [
  { key: 'name', label: 'Nome', required: true, aliases: ['nome', 'name', 'cliente', 'nomecompleto', 'razaosocial'], example: 'Empresa ABC, Lda' },
  { key: 'email', label: 'Email', required: true, aliases: ['email', 'correio', 'mail'], example: 'contacto@empresaabc.com' },
  { key: 'phone', label: 'Telefone', aliases: ['telefone', 'phone', 'celular', 'telemovel', 'contacto', 'whatsapp'], example: '+258 82 987 6543' },
  { key: 'contactPerson', label: 'Contacto / Gestor', aliases: ['gestor', 'contacto', 'contactperson', 'pessoa'], example: 'Maria de Fátima' },
  { key: 'taxId', label: 'NUIT / NIF', aliases: ['nuit', 'nif', 'taxid', 'tax', 'nuif'], example: '4001234567' },
  { key: 'vatNumber', label: 'VAT', aliases: ['vat', 'vatnumber', 'iva'], example: '' },
  { key: 'notes', label: 'Notas', aliases: ['notas', 'notes', 'observacoes', 'obs'], example: 'Cliente preferencial, entrega no escritório central' },
];

// ID do usuário logado (substituir pela lógica real do seu AuthContext/JWT)
const LOGGED_USER_ID = "69ad5bec5ecbc09bc1596e3f"; 
export const Clients: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [sales, setSales] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showOnlyMine, setShowOnlyMine] = useState(false);
  const [originFilter, setOriginFilter] = useState('all');
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pages: 1,
    total: 0,
  });
  // ── Carteira / Crédito ──
  const [walletClient, setWalletClient] = useState<Client | null>(null);
  const [walletTab, setWalletTab] = useState<'deposit' | 'credit'>('deposit');
  const [depositForm, setDepositForm] = useState({ amount: '', method: 'Cash', note: '' });
  const [creditForm, setCreditForm] = useState({
    creditEnabled: false,
    creditLimit: '',
    creditPeriod: 'month',
    commitmentReference: '',
  });
  const [commitmentFile, setCommitmentFile] = useState<{ name: string; data: string } | null>(null);
  const [savingWallet, setSavingWallet] = useState(false);
// ── NOVO ── Seleção múltipla de clientes
  const [selectedClientIds, setSelectedClientIds] = useState<Set<string>>(new Set());
  const [showProposalModal, setShowProposalModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  const selectedClientsForProposal = clients.filter(c => selectedClientIds.has(c._id));

  const toggleClientSelection = (clientId: string) => {
    setSelectedClientIds(prev => {
      const next = new Set(prev);
      if (next.has(clientId)) {
        next.delete(clientId);
      } else {
        next.add(clientId);
      }
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedClientIds(new Set());
  };
  const loadData = async (page: number) => {
    try {
      setLoading(true);
      const [clientsRes, salesRes] = await Promise.all([
        clientsApi.getAll({ 
          page, 
          limit: 12, 
          search, 
          origin: originFilter,
          onlyMine: showOnlyMine 
        }),
        salesApi.getAll({ limit: 1000 })
      ]);
      
      setClients(clientsRes.clients);
      setPagination(clientsRes.pagination);
      setSales(salesRes.sales || []);

      // Limpa seleção ao mudar de página/filtro (opcional – pode remover se preferir manter)
      setSelectedClientIds(new Set());
    } catch (error) {
      toast.error('Erro ao carregar os dados dos clientes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      loadData(1);
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [search, originFilter, showOnlyMine]);

  const handleExportExcel = async () => {
    try {
      const res = await clientsApi.getAll({ limit: 100000, search: '' });
      const allClients = res.clients || [];
      if (allClients.length === 0) {
        toast.error('Não existem clientes para exportar.');
        return;
      }
      exportToExcel(
        [
          { key: 'name', label: 'Nome' },
          { key: 'email', label: 'Email' },
          { key: 'phone', label: 'Telefone' },
          { key: 'contactPerson', label: 'Contacto / Gestor' },
          { key: 'taxId', label: 'NUIT / NIF' },
          { key: 'vatNumber', label: 'VAT' },
          { key: 'balance', label: 'Saldo Wallet (MT)' },
          { key: 'creditLimit', label: 'Limite de Crédito (MT)' },
          { key: 'notes', label: 'Notas' },
        ],
        allClients,
        `clientes-${new Date().toISOString().slice(0, 10)}.xlsx`,
        'Clientes'
      );
      toast.success(`${allClients.length} clientes exportados.`);
    } catch (error) {
      toast.error('Erro ao exportar clientes.');
    }
  };

  const calculateTotalSpent = (clientId: string) => {
    return sales
      .filter(sale => (sale.customer?._id === clientId || sale.customerId === clientId))
      .reduce((acc, sale) => acc + (sale.total || 0), 0);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja remover este registro?')) return;
    try {
      await clientsApi.delete(id);
      toast.success('Cliente removido com sucesso');
      loadData(pagination.current);
      setOpenMenu(null);
    } catch (error) {
      toast.error('Erro ao eliminar cliente');
    }
  };

  const handleTopUp = async (client: Client) => {
    setWalletClient(client);
    setWalletTab('deposit');
    setDepositForm({ amount: '', method: 'Cash', note: '' });
    setOpenMenu(null);
  };

  const openCreditConfig = (client: Client) => {
    setWalletClient(client);
    setWalletTab('credit');
    setCreditForm({
      creditEnabled: client.creditEnabled ?? false,
      creditLimit: client.creditLimit ? String(client.creditLimit) : '',
      creditPeriod: client.creditPeriod || 'month',
      commitmentReference: client.commitmentTerm?.reference || '',
    });
    setCommitmentFile(null);
    setOpenMenu(null);
  };

  const submitDeposit = async () => {
    if (!walletClient) return;
    const amt = Number(depositForm.amount);
    if (!amt || amt <= 0) {
      toast.error('Informe um valor válido');
      return;
    }
    try {
      setSavingWallet(true);
      await clientsApi.topUp(walletClient._id, amt, {
        method: depositForm.method,
        note: depositForm.note,
      });
      toast.success('Depósito adiantado registado com sucesso');
      setWalletClient(null);
      loadData(pagination.current);
    } catch (err) {
      toast.error('Erro ao processar depósito');
    } finally {
      setSavingWallet(false);
    }
  };

  const submitCredit = async () => {
    if (!walletClient) return;
    try {
      setSavingWallet(true);
      const payload: any = {
        creditEnabled: creditForm.creditEnabled,
        creditLimit: creditForm.creditLimit ? Number(creditForm.creditLimit) : 0,
        creditPeriod: creditForm.creditPeriod,
      };
      if (creditForm.commitmentReference.trim() || commitmentFile) {
        payload.commitmentTerm = {
          reference: creditForm.commitmentReference.trim(),
          fileName: commitmentFile?.name || '',
          fileData: commitmentFile?.data || '',
          signedAt: new Date().toISOString(),
        };
      }
      await clientsApi.setCredit(walletClient._id, payload);
      toast.success('Configuração de crédito guardada com sucesso');
      setWalletClient(null);
      loadData(pagination.current);
    } catch (err) {
      toast.error('Erro ao guardar configuração de crédito');
    } finally {
      setSavingWallet(false);
    }
  };

  const handleCommitmentFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Ficheiro demasiado grande (máx. 2MB)');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setCommitmentFile({ name: file.name, data: String(reader.result) });
    reader.readAsDataURL(file);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header Estilo Brendkit */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight uppercase">Base de Clientes</h1>
          <p className="mt-1 text-sm text-slate-500 font-medium">Gestão de saldos, histórico e fidelização</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link
            to="/clients/new"
            className="inline-flex items-center px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-xs font-black uppercase tracking-widest transition-all shadow-lg active:scale-95"
          >
            <Plus className="w-4 h-4 mr-2" /> Novo Cliente
          </Link>

          <button
            disabled={selectedClientIds.size === 0}
            onClick={() => setShowProposalModal(true)}
            className={`
              inline-flex items-center px-5 py-2.5 
              ${selectedClientIds.size === 0 
                ? 'bg-gray-300 cursor-not-allowed' 
                : 'bg-green-600 hover:bg-green-700'} 
              text-white rounded-md text-xs font-black uppercase tracking-widest 
              transition-all shadow-lg active:scale-95
            `}
          >
            <Send className="w-4 h-4 mr-2" />
            Enviar Proposta {selectedClientIds.size > 0 && `(${selectedClientIds.size})`}
          </button>

          <button
            onClick={handleExportExcel}
            className="inline-flex items-center px-5 py-2.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-md text-xs font-black uppercase tracking-widest transition-all shadow-sm active:scale-95"
          >
            <FileDown className="w-4 h-4 mr-2" /> Exportar Excel
          </button>

          <button
            onClick={() => setShowImportModal(true)}
            className="inline-flex items-center px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-xs font-black uppercase tracking-widest transition-all shadow-lg active:scale-95"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Importar Excel
          </button>

          {selectedClientIds.size > 0 && (
            <button
              onClick={clearSelection}
              className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1"
            >
              <X size={16} /> Limpar
            </button>
          )}
        </div>
      </div>

      {/* Barra de Filtros Avançada */}
      <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-8 flex flex-col md:flex-row gap-4 shadow-sm">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Pesquisar por nome, email ou NUIT..."
            className="w-full pl-10 pr-4 py-3 bg-slate-50 border-none rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm font-medium"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        
        <div className="flex gap-2">
          <select 
            className="bg-slate-50 border-none rounded-xl px-4 py-3 text-xs font-black text-slate-600 outline-none focus:ring-2 focus:ring-blue-500 uppercase tracking-wider"
            value={originFilter}
            onChange={(e) => setOriginFilter(e.target.value)}
          >
            <option value="all">Todas as Origens</option>
            <option value="internal">Equipa Interna</option>
            <option value="external">Portal Externo</option>
          </select>

          <button
            onClick={() => setShowOnlyMine(!showOnlyMine)}
            className={`flex items-center gap-2 px-5 py-3 rounded-xl border-none text-[10px] font-black uppercase tracking-widest transition-all ${
              showOnlyMine 
                ? 'bg-blue-600 text-white shadow-md' 
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            <Filter size={14} />
            {showOnlyMine ? 'Meus Registos' : 'Todos'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 animate-pulse">
          {[...Array(6)].map((_, i) => <div key={i} className="bg-slate-50 h-64 rounded-2xl border border-slate-100" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {clients.map((client) => {
            const totalSpent = calculateTotalSpent(client._id);
            const isOwner = client.createdBy?.$oid === LOGGED_USER_ID || client.createdBy === LOGGED_USER_ID;
            const isSelected = selectedClientIds.has(client._id);

            return (
              <div key={client._id} className="bg-white rounded-2xl border border-slate-100 p-6 hover:border-blue-200 hover:shadow-2xl hover:shadow-slate-100 transition-all group relative flex flex-col justify-between">
                {/* Checkbox de seleção */}
                <div className="absolute top-6 right-12 z-10">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleClientSelection(client._id);
                    }}
                    className="p-1 rounded hover:bg-slate-100 transition"
                  >
                    {isSelected ? (
                      <CheckSquare size={22} className="text-green-600" />
                    ) : (
                      <Square size={22} className="text-slate-400 hover:text-slate-600" />
                    )}
                  </button>
                </div>
                <div>
                  <div className="flex justify-between items-start mb-5">
                    <div className="flex items-center gap-4">
                      <div className="w-8 h-8 bg-blue-600 rounded-md flex items-center justify-center text-white font-black text-md shadow-inner">
                        {client.name.charAt(0)}
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900 truncate max-w-[140px] group-hover:text-blue-600 transition tracking-tight">
                          {client.name}
                        </h3>
                        {isOwner && (
                          <span className="text-[9px] font-black text-blue-600 uppercase tracking-widest bg-blue-50 px-2 py-0.5 rounded-full">
                            Gestor Direto
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="relative">
                      <button 
                        onClick={() => setOpenMenu(openMenu === client._id ? null : client._id)}
                        className="p-2 hover:bg-slate-50 rounded-xl transition"
                      >
                        <MoreVertical size={20} className="text-slate-400" />
                      </button>
                      {openMenu === client._id && (
                        <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-2xl border border-slate-100 z-30 overflow-hidden py-1">
                          <button onClick={() => handleTopUp(client)} className="flex items-center gap-3 w-full px-4 py-3 text-xs font-bold text-slate-700 hover:bg-slate-50 transition">
                            <Wallet className="w-4 h-4 text-emerald-500" /> Depósito Adiantado
                          </button>
                          <button onClick={() => openCreditConfig(client)} className="flex items-center gap-3 w-full px-4 py-3 text-xs font-bold text-slate-700 hover:bg-slate-50 transition">
                            <CreditCard className="w-4 h-4 text-indigo-500" /> Configurar Crédito
                          </button>
                          <Link to={`/clients/${client._id}/edit`} className="flex items-center gap-3 w-full px-4 py-3 text-xs font-bold text-slate-700 hover:bg-slate-50 transition">
                            <Edit className="w-4 h-4 text-blue-500" /> Editar Perfil
                          </Link>
                          <div className="h-px bg-slate-50 my-1" />
                          <button onClick={() => handleDelete(client._id)} className="flex items-center gap-3 w-full px-4 py-3 text-xs font-bold text-red-600 hover:bg-red-50 transition">
                            <Trash2 className="w-4 h-4" /> Eliminar Registo
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-3 text-xs font-medium text-slate-500 truncate">
                      <Mail size={14} className="text-slate-300" /> {client.email}
                    </div>
                    <div className="flex items-center gap-3 text-xs font-medium text-slate-500">
                      <Phone size={14} className="text-slate-300" /> {client.phone || 'Sem contacto'}
                    </div>
                    
                    {/* INFO DE SALDO (WALLET) */}
                    <div className="mt-5 p-3 bg-emerald-50/50 rounded-xl border border-emerald-100 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Wallet size={14} className="text-emerald-600" />
                        <span className="text-[10px] font-black text-emerald-700 uppercase tracking-widest">Saldo Wallet:</span>
                      </div>
                      <span className="font-black text-emerald-700 text-sm">
                        {new Intl.NumberFormat('pt-MZ', { style: 'currency', currency: 'MZN' }).format(client.balance || 0)}
                      </span>
                    </div>

                    {/* INFO DE CRÉDITO (SE CONFIGURADO) */}
                    {client.creditEnabled && (
                      <div className="p-3 bg-indigo-50/50 rounded-xl border border-indigo-100">
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2">
                            <CreditCard size={14} className="text-indigo-600" />
                            <span className="text-[10px] font-black text-indigo-700 uppercase tracking-widest">
                              Crédito {(client.creditPeriod === 'week' ? '/semana' : '/mês')}
                            </span>
                          </div>
                          <span className={`text-[9px] font-black px-2 py-0.5 rounded-full uppercase ${(client.creditUsed || 0) >= (client.creditLimit || 0) ? 'bg-red-100 text-red-700' : 'bg-indigo-100 text-indigo-700'}`}>
                            {(client.creditUsed || 0) >= (client.creditLimit || 0) ? 'Limite atingido' : 'Ativo'}
                          </span>
                        </div>
                        <div className="flex justify-between text-xs font-bold text-indigo-900 mb-1.5">
                          <span>{new Intl.NumberFormat('pt-MZ', { style: 'currency', currency: 'MZN' }).format(client.creditUsed || 0)}</span>
                          <span className="text-indigo-400">/ {new Intl.NumberFormat('pt-MZ', { style: 'currency', currency: 'MZN' }).format(client.creditLimit || 0)}</span>
                        </div>
                        <div className="h-1.5 bg-indigo-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${(client.creditUsed || 0) >= (client.creditLimit || 0) ? 'bg-red-500' : 'bg-indigo-500'}`}
                            style={{ width: `${Math.min(100, ((client.creditUsed || 0) / (client.creditLimit || 1)) * 100)}%` }}
                          />
                        </div>
                        {client.commitmentTerm?.reference && (
                          <p className="text-[9px] font-bold text-indigo-500 mt-1.5">
                            Termo: #{client.commitmentTerm.reference}
                          </p>
                        )}
                      </div>
                    )}

                    {/* INFO DE FATURAÇÃO TOTAL */}
                    <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <DollarSign size={14} className="text-slate-400" />
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Gasto Total:</span>
                      </div>
                      <span className="font-black text-slate-900 text-sm">
                        {new Intl.NumberFormat('pt-MZ', { style: 'currency', currency: 'MZN' }).format(totalSpent)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-slate-50">
                  <span className={`text-[9px] font-black px-2.5 py-1 rounded-full uppercase tracking-widest ${client.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                    {client.isActive ? 'Conta Ativa' : 'Inativo'}
                  </span>
                  <div className="text-[10px] text-slate-400 font-mono font-bold">
                    NUUIT: {client.taxId || 'N/A'}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Paginação Estilo Brendkit */}
      <div className="mt-12 flex flex-col md:flex-row items-center justify-between border-t border-slate-100 pt-8 gap-4">
        <div className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">
          Total Base: <span className="text-slate-900">{pagination.total}</span> Registos
        </div>
        <div className="flex items-center gap-2">
          <button
            disabled={pagination.current === 1}
            onClick={() => loadData(pagination.current - 1)}
            className="px-6 py-2.5 bg-white border border-slate-200 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-600 hover:bg-slate-50 disabled:opacity-30 transition-all active:scale-95"
          >
            Anterior
          </button>
          <div className="px-5 py-2.5 text-[10px] font-black text-blue-600 bg-blue-50 rounded-xl border border-blue-100 uppercase tracking-widest">
            {pagination.current} / {pagination.pages}
          </div>
          <button
            disabled={pagination.current === pagination.pages}
            onClick={() => loadData(pagination.current + 1)}
            className="px-6 py-2.5 bg-white border border-slate-200 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-600 hover:bg-slate-50 disabled:opacity-30 transition-all active:scale-95"
          >
            Próximo
          </button>
        </div>
      </div>
      {showProposalModal && (
        <ShareProposalModal
          isOpen={showProposalModal}
          onClose={() => setShowProposalModal(false)}
          initialSelected={selectedClientsForProposal}
          mode="clients"
          title="Enviar Proposta a Clientes"
        />
      )}

      {/* Modal de importação Excel */}
      <ExcelImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Importar Clientes de Excel"
        entityLabel="cliente"
        templateFilename="template-clientes.xlsx"
        fieldDefs={CLIENT_IMPORT_FIELDS}
        importFn={(items) => clientsApi.import(items)}
        onSuccess={() => loadData(pagination.current)}
      />

      {/* Modal de Carteira / Crédito */}
      {walletClient && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-500 to-indigo-600 p-5 flex justify-between items-center">
              <div>
                <h3 className="text-white font-black uppercase tracking-tight">Carteira do Cliente</h3>
                <p className="text-sm text-emerald-100 font-medium">{walletClient.name}</p>
              </div>
              <button onClick={() => setWalletClient(null)} className="p-2 hover:bg-white/20 rounded-lg transition">
                <X size={20} className="text-white" />
              </button>
            </div>

            <div className="p-4 border-b border-gray-100 flex gap-2">
              <button
                onClick={() => setWalletTab('deposit')}
                className={`flex-1 py-2.5 text-xs font-black uppercase rounded-xl transition ${
                  walletTab === 'deposit' ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-500'
                }`}
              >
                <Wallet className="inline w-4 h-4 mr-1" /> Depósito
              </button>
              <button
                onClick={() => setWalletTab('credit')}
                className={`flex-1 py-2.5 text-xs font-black uppercase rounded-xl transition ${
                  walletTab === 'credit' ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-500'
                }`}
              >
                <CreditCard className="inline w-4 h-4 mr-1" /> Crédito
              </button>
            </div>

            {walletTab === 'deposit' ? (
              <div className="p-5 space-y-4">
                <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-100 flex justify-between items-center">
                  <span className="text-xs font-black text-emerald-700 uppercase">Saldo atual</span>
                  <span className="font-black text-emerald-700 text-lg">
                    {new Intl.NumberFormat('pt-MZ', { style: 'currency', currency: 'MZN' }).format(walletClient.balance || 0)}
                  </span>
                </div>
                <div>
                  <label className="block text-[10px] font-black text-gray-500 uppercase mb-1">Valor do depósito (MT)</label>
                  <input
                    type="number"
                    value={depositForm.amount}
                    onChange={(e) => setDepositForm({ ...depositForm, amount: e.target.value })}
                    placeholder="0"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl text-base font-bold focus:ring-2 focus:ring-emerald-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-gray-500 uppercase mb-1">Método</label>
                  <select
                    value={depositForm.method}
                    onChange={(e) => setDepositForm({ ...depositForm, method: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm font-bold focus:ring-2 focus:ring-emerald-500 outline-none"
                  >
                    <option value="Cash">Cash (Dinheiro)</option>
                    <option value="M-Pesa">M-Pesa</option>
                    <option value="E-Mola">E-Mola</option>
                    <option value="Transferência">Transferência</option>
                    <option value="POS">POS</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-black text-gray-500 uppercase mb-1">Nota (opcional)</label>
                  <input
                    type="text"
                    value={depositForm.note}
                    onChange={(e) => setDepositForm({ ...depositForm, note: e.target.value })}
                    placeholder="Ex: Depósito adiantado de fim de mês"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm font-medium focus:ring-2 focus:ring-emerald-500 outline-none"
                  />
                </div>
                <button
                  onClick={submitDeposit}
                  disabled={savingWallet}
                  className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-sm rounded-xl transition disabled:opacity-50"
                >
                  {savingWallet ? 'A guardar...' : 'Registar Depósito Adiantado'}
                </button>
              </div>
            ) : (
              <div className="p-5 space-y-4">
                <label className="flex items-center justify-between p-3 bg-indigo-50 rounded-xl border border-indigo-100 cursor-pointer">
                  <span className="text-xs font-black text-indigo-700 uppercase">Ativar crédito assinado</span>
                  <input
                    type="checkbox"
                    checked={creditForm.creditEnabled}
                    onChange={(e) => setCreditForm({ ...creditForm, creditEnabled: e.target.checked })}
                    className="w-5 h-5 text-indigo-600 rounded"
                  />
                </label>
                <div>
                  <label className="block text-[10px] font-black text-gray-500 uppercase mb-1">Limite de crédito (MT)</label>
                  <input
                    type="number"
                    value={creditForm.creditLimit}
                    onChange={(e) => setCreditForm({ ...creditForm, creditLimit: e.target.value })}
                    placeholder="Ex: 50000"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl text-base font-bold focus:ring-2 focus:ring-indigo-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-gray-500 uppercase mb-1">Período de crédito</label>
                  <select
                    value={creditForm.creditPeriod}
                    onChange={(e) => setCreditForm({ ...creditForm, creditPeriod: e.target.value as any })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm font-bold focus:ring-2 focus:ring-indigo-500 outline-none"
                  >
                    <option value="month">Por mês</option>
                    <option value="week">Por semana</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-black text-gray-500 uppercase mb-1">Referência do termo de compromisso</label>
                  <input
                    type="text"
                    value={creditForm.commitmentReference}
                    onChange={(e) => setCreditForm({ ...creditForm, commitmentReference: e.target.value })}
                    placeholder="Ex: TC-2026-001"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm font-medium focus:ring-2 focus:ring-indigo-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-gray-500 uppercase mb-1">Termo de compromisso (anexo)</label>
                  <input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={handleCommitmentFile}
                    className="w-full text-xs font-medium text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-indigo-50 file:text-indigo-700 file:font-black file:uppercase file:text-[10px] hover:file:bg-indigo-100"
                  />
                  {commitmentFile && (
                    <p className="text-[10px] font-bold text-indigo-600 mt-1">✓ {commitmentFile.name}</p>
                  )}
                </div>
                <button
                  onClick={submitCredit}
                  disabled={savingWallet}
                  className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-black uppercase text-sm rounded-xl transition disabled:opacity-50"
                >
                  {savingWallet ? 'A guardar...' : 'Guardar Configuração de Crédito'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};